import React, { useState, useMemo } from 'react';
import { useSRMS } from '../../context/SRMSContext';
import {
  Trash2,
  AlertTriangle,
  Users,
  Building2,
  Layers,
  CheckCircle2,
  X,
  Archive,
  ShieldAlert,
  ArrowRight,
} from 'lucide-react';

interface BulkDeleteAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialScope?: 'all' | 'agent' | 'branch' | 'selected';
  selectedAccountIds?: string[];
  preselectedAgentId?: string;
  preselectedBranchName?: string;
  onSuccess?: (deletedCount: number, scope: string) => void;
}

export const BulkDeleteAccountsModal: React.FC<BulkDeleteAccountsModalProps> = ({
  isOpen,
  onClose,
  initialScope = 'agent',
  selectedAccountIds = [],
  preselectedAgentId,
  preselectedBranchName,
  onSuccess,
}) => {
  const { accounts, users, branches, deleteBulkAccounts } = useSRMS();

  const [scope, setScope] = useState<'agent' | 'branch' | 'all' | 'selected'>(
    selectedAccountIds.length > 0 && initialScope === 'selected' ? 'selected' : initialScope
  );
  const [selectedAgentId, setSelectedAgentId] = useState<string>(preselectedAgentId || '');
  const [selectedBranch, setSelectedBranch] = useState<string>(preselectedBranchName || '');
  const [deleteMode, setDeleteMode] = useState<'archive' | 'permanent'>('archive');
  const [confirmText, setConfirmText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [resultMessage, setResultMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // List of unique agents with accounts
  const agentOptions = useMemo(() => {
    const countsMap = new Map<string, { count: number; name: string; overdue: number }>();
    accounts.forEach((acc) => {
      const aId = acc.assignedAgentId || 'unassigned';
      const aName = acc.assignedAgentName || 'Unassigned / Queue';
      const current = countsMap.get(aId) || { count: 0, name: aName, overdue: 0 };
      countsMap.set(aId, {
        count: current.count + 1,
        name: current.name || aName,
        overdue: current.overdue + (acc.overdueAmount || 0),
      });
    });

    const list: Array<{ id: string; name: string; count: number; overdue: number }> = [];
    countsMap.forEach((val, id) => {
      // Find full user if available
      const u = users.find((user) => user.agentId === id || user.id === id);
      list.push({
        id,
        name: u?.name || val.name,
        count: val.count,
        overdue: val.overdue,
      });
    });

    return list.sort((a, b) => b.count - a.count);
  }, [accounts, users]);

  // List of unique branches with accounts
  const branchOptions = useMemo(() => {
    const countsMap = new Map<string, { count: number; overdue: number }>();
    accounts.forEach((acc) => {
      const b = acc.branch || 'Head Office / Unspecified';
      const current = countsMap.get(b) || { count: 0, overdue: 0 };
      countsMap.set(b, {
        count: current.count + 1,
        overdue: current.overdue + (acc.overdueAmount || 0),
      });
    });

    const list: Array<{ name: string; count: number; overdue: number }> = [];
    countsMap.forEach((val, name) => {
      list.push({
        name,
        count: val.count,
        overdue: val.overdue,
      });
    });

    return list.sort((a, b) => b.count - a.count);
  }, [accounts]);

  // Target accounts calculation based on current scope
  const matchingAccounts = useMemo(() => {
    if (scope === 'all') {
      return accounts;
    }
    if (scope === 'selected') {
      const idSet = new Set(selectedAccountIds);
      return accounts.filter((a) => idSet.has(a.accountId) || idSet.has(a.id));
    }
    if (scope === 'agent') {
      if (!selectedAgentId) return [];
      return accounts.filter(
        (a) =>
          a.assignedAgentId === selectedAgentId ||
          (selectedAgentId === 'unassigned' && (!a.assignedAgentId || a.assignedAgentId === ''))
      );
    }
    if (scope === 'branch') {
      if (!selectedBranch) return [];
      return accounts.filter((a) => (a.branch || 'Head Office / Unspecified') === selectedBranch);
    }
    return [];
  }, [scope, accounts, selectedAgentId, selectedBranch, selectedAccountIds]);

  const targetCount = matchingAccounts.length;
  const targetOverdueTotal = useMemo(() => {
    return matchingAccounts.reduce((sum, a) => sum + (a.overdueAmount || 0), 0);
  }, [matchingAccounts]);

  const targetOutstandingTotal = useMemo(() => {
    return matchingAccounts.reduce((sum, a) => sum + (a.outstandingAmount || 0), 0);
  }, [matchingAccounts]);

  const formatINR = (amt: number) => {
    return `₹${Math.round(amt).toLocaleString('en-IN')}`;
  };

  const requiredConfirmWord = scope === 'all' ? 'DELETE ALL' : 'DELETE';
  const isConfirmValid = confirmText.trim().toUpperCase() === requiredConfirmWord;

  const handleExecuteBulkDelete = async () => {
    if (targetCount === 0) return;
    if (!isConfirmValid) return;

    setIsProcessing(true);
    setResultMessage(null);

    try {
      const res = await deleteBulkAccounts({
        scope: scope === 'selected' ? 'ids' : scope,
        agentId: scope === 'agent' ? selectedAgentId : undefined,
        agentName: scope === 'agent' ? agentOptions.find((a) => a.id === selectedAgentId)?.name : undefined,
        branchName: scope === 'branch' ? selectedBranch : undefined,
        accountIds: scope === 'selected' ? selectedAccountIds : undefined,
        permanent: deleteMode === 'permanent',
      });

      if (res.success) {
        setResultMessage({ type: 'success', text: res.message });
        if (onSuccess) {
          onSuccess(res.count, scope);
        }
        setTimeout(() => {
          onClose();
          setConfirmText('');
          setResultMessage(null);
        }, 1400);
      } else {
        setResultMessage({ type: 'error', text: res.message });
      }
    } catch (err: any) {
      setResultMessage({ type: 'error', text: err?.message || 'Bulk deletion failed. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-y-auto max-h-[92vh] border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-rose-600 via-rose-700 to-red-800 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-white/10 rounded-xl">
              <Trash2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold">Bulk Account Deletion & Cleanup</h3>
              <p className="text-rose-100 text-xs">Delete bulk accounts by Agent, Branch, Selection, or All</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-rose-200 hover:text-white p-1 rounded-lg cursor-pointer transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 text-xs">
          {/* Scope Selector Tabs */}
          <div>
            <label className="block text-slate-700 font-bold mb-2">Select Bulk Deletion Scope:</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => {
                  setScope('agent');
                  setConfirmText('');
                }}
                className={`p-2.5 rounded-xl border text-left flex flex-col gap-1 transition cursor-pointer ${
                  scope === 'agent'
                    ? 'border-rose-500 bg-rose-50/80 text-rose-950 font-bold shadow-xs'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-rose-600" />
                  <span className="text-xs">Specific Agent</span>
                </div>
                <span className="text-[10px] text-slate-500">By agent allocation</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setScope('branch');
                  setConfirmText('');
                }}
                className={`p-2.5 rounded-xl border text-left flex flex-col gap-1 transition cursor-pointer ${
                  scope === 'branch'
                    ? 'border-rose-500 bg-rose-50/80 text-rose-950 font-bold shadow-xs'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-rose-600" />
                  <span className="text-xs">Specific Branch</span>
                </div>
                <span className="text-[10px] text-slate-500">By bank branch</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setScope('all');
                  setConfirmText('');
                }}
                className={`p-2.5 rounded-xl border text-left flex flex-col gap-1 transition cursor-pointer ${
                  scope === 'all'
                    ? 'border-rose-600 bg-rose-100 text-rose-950 font-bold shadow-xs'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4 text-rose-700" />
                  <span className="text-xs">All Accounts</span>
                </div>
                <span className="text-[10px] text-rose-600 font-semibold">Entire Database</span>
              </button>

              {selectedAccountIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setScope('selected');
                    setConfirmText('');
                  }}
                  className={`p-2.5 rounded-xl border text-left flex flex-col gap-1 transition cursor-pointer ${
                    scope === 'selected'
                      ? 'border-rose-500 bg-rose-50/80 text-rose-950 font-bold shadow-xs'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-rose-600" />
                    <span className="text-xs">Selected ({selectedAccountIds.length})</span>
                  </div>
                  <span className="text-[10px] text-slate-500">Table checkmarks</span>
                </button>
              )}
            </div>
          </div>

          {/* Scope Specific Controls */}
          {scope === 'agent' && (
            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2">
              <label className="block text-slate-800 font-bold">Choose Recovery Agent:</label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500"
              >
                <option value="">-- Select an Agent to Clear Accounts --</option>
                {agentOptions.map((ag) => (
                  <option key={ag.id} value={ag.id}>
                    {ag.name} ({ag.id}) — {ag.count} accounts ({formatINR(ag.overdue)})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500">
                Only accounts assigned to this agent will be removed. Other agents' accounts will remain untouched.
              </p>
            </div>
          )}

          {scope === 'branch' && (
            <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-2">
              <label className="block text-slate-800 font-bold">Choose Branch:</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg p-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500"
              >
                <option value="">-- Select a Branch to Clear Accounts --</option>
                {branchOptions.map((br) => (
                  <option key={br.name} value={br.name}>
                    {br.name} — {br.count} accounts ({formatINR(br.overdue)})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500">
                Only accounts mapped to this branch will be removed. All other branches remain intact.
              </p>
            </div>
          )}

          {scope === 'all' && (
            <div className="p-3.5 bg-rose-50 rounded-xl border border-rose-200 space-y-2">
              <div className="flex items-center gap-2 text-rose-900 font-bold">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                <span>Permanent Database Account Wipe</span>
              </div>
              <p className="text-slate-600 text-[11px] leading-relaxed">
                You are preparing to delete <b>all {accounts.length} customer accounts</b> from local and permanent storage.
              </p>
              <div className="p-2 bg-white/90 rounded-lg border border-rose-100 text-[11px] space-y-1">
                <p className="text-emerald-700 font-semibold">✓ Safe: Users, agent logins, branch settings, and commission rules will NOT be deleted.</p>
                <p className="text-rose-700 font-semibold">✗ Wiped: All customer account records, balances, and loan details will be reset to 0.</p>
              </div>
            </div>
          )}

          {scope === 'selected' && (
            <div className="p-3.5 bg-blue-50 rounded-xl border border-blue-200 space-y-1">
              <div className="flex items-center gap-2 text-blue-900 font-bold">
                <Layers className="w-4 h-4 text-blue-600" />
                <span>Selected Accounts Deletion</span>
              </div>
              <p className="text-slate-600 text-[11px]">
                You have selected <b>{selectedAccountIds.length} specific account(s)</b> from the master table for deletion.
              </p>
            </div>
          )}

          {/* Impact Summary Card */}
          <div className="p-3.5 bg-slate-900 text-white rounded-xl space-y-2">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <span className="text-slate-400 font-medium">Matching Accounts to Delete:</span>
              <span className="text-base font-extrabold font-mono text-rose-400">{targetCount} Account(s)</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] pt-1">
              <div>
                <span className="text-slate-400 block">Total Overdue:</span>
                <span className="font-mono font-bold text-red-400">{formatINR(targetOverdueTotal)}</span>
              </div>
              <div>
                <span className="text-slate-400 block">Total Outstanding:</span>
                <span className="font-mono font-bold text-slate-200">{formatINR(targetOutstandingTotal)}</span>
              </div>
            </div>
          </div>

          {/* Archive vs Permanent Option */}
          <div className="space-y-1.5">
            <label className="block text-slate-700 font-bold">Deletion Method:</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setDeleteMode('archive')}
                className={`p-2.5 rounded-xl border text-left flex items-start gap-2 transition cursor-pointer ${
                  deleteMode === 'archive'
                    ? 'border-emerald-500 bg-emerald-50/70 text-emerald-950 font-bold'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <Archive className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-emerald-900">Archive (Recommended)</div>
                  <div className="text-[10px] text-slate-500 font-normal">
                    Removes from active lists while preserving historical remarks, PTPs, and audit trail.
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setDeleteMode('permanent')}
                className={`p-2.5 rounded-xl border text-left flex items-start gap-2 transition cursor-pointer ${
                  deleteMode === 'permanent'
                    ? 'border-rose-500 bg-rose-50/70 text-rose-950 font-bold'
                    : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <Trash2 className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-bold text-rose-900">Permanent Hard Delete</div>
                  <div className="text-[10px] text-slate-500 font-normal">
                    Permanently purges the records from storage. Cannot be recovered locally.
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Safety Verification Input */}
          {targetCount > 0 && (
            <div className="space-y-1.5 bg-rose-50/50 p-3 rounded-xl border border-rose-200">
              <label className="block text-rose-950 font-bold">
                Security Confirmation: Type <span className="font-mono text-rose-700 font-extrabold bg-rose-100 px-1.5 py-0.5 rounded">{requiredConfirmWord}</span> to proceed:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={`Type ${requiredConfirmWord}`}
                className="w-full bg-white border border-rose-300 rounded-lg p-2 text-xs font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-500"
              />
            </div>
          )}

          {/* Result Alert */}
          {resultMessage && (
            <div
              className={`p-3 rounded-xl border text-xs font-medium flex items-center gap-2 ${
                resultMessage.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900'
              }`}
            >
              {resultMessage.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
              )}
              <span>{resultMessage.text}</span>
            </div>
          )}

          {/* Footer Action Buttons */}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExecuteBulkDelete}
              disabled={isProcessing || targetCount === 0 || !isConfirmValid}
              className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold flex items-center gap-2 shadow-sm transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              <span>
                {isProcessing
                  ? 'Processing Bulk Deletion...'
                  : targetCount === 0
                  ? 'Select Criteria'
                  : `Delete ${targetCount} Account${targetCount > 1 ? 's' : ''}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
